import { db } from '../firebase';
import { USE_CONTRACTS_V2, v2RecordsByIds, v2BusinessBayDocs, v2AirportRows, v2AirportOpen, v2AirportClosedPage, v2MonthlyOpenWithAsOf, v2MonthlyChangedSinceWithAsOf, changesSinceOf, v2MonthlySearch, v2MonthlyByIds, v2AirportByIds, v2MonthlyDocsSince, v2AirportRowsSince, v2AirportByDays, v2ExactRecords, toLegacyContract, toLegacyAirport, forgetClosedReads } from './contractsV2';   // local test of the new store
import { withEjarVehicles } from './storeData';
// the fleet, maintenance logs and Invygo cars on the new store (USE_CONTRACTS_V2)
import { storeCars, storeCarByPlate, storeSaveCar, storeCarBatch, masterSyncWrites, conflictText, storeLogs, storeAddLog, storeSaveLog, storeDeleteLog, logVersionOf, storeInvygoCars, storeInvygoByIds, invygoId } from './fleetStore';
import { finesService, officeError } from './finesService';   // the office records (staff) on the new store
import { collection, getDocs, doc, getDoc, writeBatch, deleteDoc, setDoc, query, where, limit, orderBy, getDocsFromServer, getDocFromServer, documentId, runTransaction } from 'firebase/firestore';
import { parse, isValid } from 'date-fns';
import logger from '../utils/logger';
import { archiveService } from './archiveService';
import { cleanCarNames } from './fleetNames';
import { jsonCacheService } from './jsonCacheService';
import { cleanPhoneNumber } from '../utils/phoneUtils';
import { buildMatcherForContracts } from './contractLookup';
import { formatDateTime, parseDateTime, normalizePlate, cleanText } from './possessionMatcher';
import { contractsForInvygoId } from './invygoIds';
import { statusMapOf } from './fleetStatusMap';
import { maintenanceHash, needsSavedLog, hashAfterUpdate } from './maintenanceLogs.logic';
import { contractChangedSince, contractChangedError } from './contractVersion';

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));
const BATCH_DELAY_MS = 800;

export { db }; // Export for other modules

// Collection References
const staffCollection = collection(db, 'staff');
const fleetCollection = collection(db, 'fleet');
const contractsCollection = collection(db, 'contracts');
const maintenanceCollection = collection(db, 'maintenance_logs');
const invygoFleetCollection = collection(db, 'invygo_fleet');
const airportContractsCollection = collection(db, 'airport_contracts');

// --- INVYGO FLEET SERVICE ---
// The old Invygo sheet's cars: history, read only. The sheet is no longer uploaded (owner, 28 Sep 2026: "no more
// manual lists - everything from Ejar"); a new Invygo booking's id comes from Ejar's bookings (src/services/invygoIds.js).
export const invygoService = {
    getAllInvygoCars: async (forceRefresh = false) => {
        try {
            // the store (USE_CONTRACTS_V2): the saved copy + only what changed since its read ({id, ...car, version})
            if (USE_CONTRACTS_V2) return await storeInvygoCars({ force: forceRefresh });
            // 1. Try JSON Cache First
            if (!forceRefresh) {
                const cached = await jsonCacheService.loadInvygo();
                const isCacheValid = await jsonCacheService.isCacheValid('invygo.json', 24);
                if (cached && cached.length > 0 && isCacheValid) {
                    return cached;
                }
            }

            // 2. Load from DB
            const snapshot = await getDocs(invygoFleetCollection);
            const data = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // 3. Save to JSON Cache
            await jsonCacheService.saveInvygo(data);

            return data;
        } catch (error) {
            logger.error('Error fetching invygo fleet:', error);
            // Fallback to cache
            try {
                const cached = await jsonCacheService.loadInvygo();
                if (cached) return cached;
            } catch (e) { }
            return [];
        }
    },

    getInvygoDataByIdentifier: async (id) => {
        if (!id) return null;
        try {
            const docId = id.toString().replace(/\s/g, '').trim().toUpperCase();

            // the store (USE_CONTRACTS_V2): the car of this Booking ID, else the one whose agreement it is (the Invygo
            // cars are few: their saved copy is searched)
            if (USE_CONTRACTS_V2) {
                const byId = (await storeInvygoByIds([docId])).get(invygoId(docId));
                if (byId) return byId;
                return (await invygoService.getAllInvygoCars()).find((car) => car.agreement === id) || null;
            }

            // 1. Try direct doc match (Booking ID)
            const docRef = doc(invygoFleetCollection, docId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                return { id: docSnap.id, ...docSnap.data() };
            }
            
            // 2. Try searching by agreement field
            const q = query(invygoFleetCollection, where('agreement', '==', id));
            const snapshot = await getDocs(q);
            if (!snapshot.empty) {
                return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
            }
            
            return null;
        } catch (error) {
            logger.error('Error fetching Invygo data by identifier:', error);
            return null;
        }
    }
};

// --- STAFF SERVICE ---
// With the new store (USE_CONTRACTS_V2) the staff live in the engine's office records (GET / PATCH / DELETE
// /api/office/staff): the same rows, and a save names the version each employee was read with (409
// record-changed when someone saved it since - nothing is written). The versions of the last read are kept here
// (staff id -> _version), so an edit sends the version its row was opened with; a save moves them on.
const staffVersions = new Map();

// One employee as the table shows it (camelCase), from its stored fields; null for a deleted / inactive one
const staffRowOf = (id, data) => {
    const sNo = data.staff_no?.toString().trim(); // Force string and trim
    if (data.status === 'deleted' || data.status === 'inactive') return null;
    return {
        id,
        staffNo: sNo,
        name: data.name,
        passportNumber: data.passport_number,
        job: data.job,
        nationality: data.nationality,
        cardType: data.card_type,
        cardNumber: data.card_number,
        cardExpiry: data.card_expiry ? new Date(data.card_expiry) : null,
        passportIssueDate: data.passport_issue_date,
        passportExpireDate: data.passport_expiry,
        email: data.email,
        joiningDate: data.joining_date,
        years: data.years,
        daysUntilExpiry: calculateDaysLeft(data.card_expiry),
        status: data.status
    };
};

export const staffService = {
    /**
     * Save the edited fields of the given employees: one merge per employee, only the fields that were edited,
     * nothing else touched and nothing ever deleted. [{staffNo, fields: {name?, job?, nationality?, cardExpiry?},
     * version?}] (Until 26 Sep 2026 the table called a replace-sync here that deleted every employee NOT in the
     * list, so saving one edit wiped the rest of the staff.) Throws when the save is refused: the page never shows
     * a false success. Returns the number of employees saved.
     */
    saveStaffEdits: async (edits) => {
        const FIELDS = { name: 'name', job: 'job', nationality: 'nationality', cardExpiry: 'card_expiry' };
        const rows = [];
        const storeEdits = [];
        for (const { staffNo, fields, version } of edits || []) {
            const id = String(staffNo ?? '').trim();
            if (!id) continue;
            const data = { last_updated: new Date().toISOString() };
            const sent = {};
            for (const [key, value] of Object.entries(fields || {})) {
                if (!(key in FIELDS)) continue;
                if (key === 'cardExpiry') {
                    const d = value instanceof Date ? value : (value ? new Date(value) : null);
                    data.card_expiry = d && !isNaN(d) ? d.toISOString() : null;
                    sent.cardExpiry = data.card_expiry;
                } else {
                    data[FIELDS[key]] = value;
                    sent[key] = value;
                }
            }
            rows.push([id, data]);
            const opened = version ?? staffVersions.get(id);
            storeEdits.push({ staffNo: id, fields: sent, ...(opened === undefined ? {} : { version: opened }) });
        }
        if (USE_CONTRACTS_V2) {
            if (storeEdits.length === 0) return 0;
            // one transaction on the server: one stale employee refuses the whole save (409), nothing written
            const answer = await finesService.officeStaffEdit(storeEdits).catch((e) => { throw officeError(e); });
            Object.entries(answer?.versions || {}).forEach(([id, v]) => staffVersions.set(id, v));
            return rows.length;
        }
        const CHUNK_SIZE = 450;
        for (let i = 0; i < rows.length; i += CHUNK_SIZE) {
            if (i > 0) await sleep(BATCH_DELAY_MS);
            const batch = writeBatch(db);
            rows.slice(i, i + CHUNK_SIZE).forEach(([id, data]) => batch.set(doc(staffCollection, id), data, { merge: true }));
            await batch.commit();
        }
        return rows.length;
    },

    getAllStaff: async () => {
        if (USE_CONTRACTS_V2) {
            // the store's documents ({id, ...fields, _version}), mapped the same way; a failed read throws
            const items = await finesService.officeStaff().catch((e) => { throw officeError(e); });
            staffVersions.clear();
            return (items || []).map((item) => {
                const { id, _version, ...data } = item || {};
                if (_version !== undefined) staffVersions.set(String(id), _version);
                const row = staffRowOf(id, data);
                return row && { ...row, _version };
            }).filter(Boolean);
        }
        // FORCE SERVER to bypass cache that might be stuck
        const snapshot = await getDocsFromServer(staffCollection);

        return snapshot.docs.map(doc => staffRowOf(doc.id, doc.data())).filter(Boolean); // Remote nulls (deleted)
    },

    deleteStaff: async (staffNo) => {
        try {
            if (!staffNo) return { success: false, error: "No Staff No provided" };
            const docId = staffNo.toString().trim();

            if (USE_CONTRACTS_V2) {
                // the document of that staff number and every other one holding it, in one transaction (logged)
                await finesService.officeStaffDelete(docId).catch((e) => { throw officeError(e); });
                staffVersions.delete(docId);
                return { success: true };
            }

            // Delete by ID
            await deleteDoc(doc(staffCollection, docId));

            // Deep Purge Phase 2: Query by field to find shadow docs
            const q = query(staffCollection, where('staff_no', '==', docId));
            const fieldSnap = await getDocs(q);
            if (!fieldSnap.empty) {
                const batch = writeBatch(db);
                fieldSnap.forEach(d => {
                    batch.delete(d.ref);
                });
                await batch.commit();
            }

            // Verification (Force SERVER)
            await getDocFromServer(doc(staffCollection, docId));

            return { success: true };
        } catch (error) {
            // thrown on: the page shows the refusal instead of removing the row and saying "deleted"
            logger.error('Error deleting staff:', error);
            throw error;
        }
    }
};

// Helper: days until the card expires; null when there is no (valid) date - "Unknown", never "Expired"
const calculateDaysLeft = (expiryDateString) => {
    if (!expiryDateString) return null;
    const expiry = new Date(expiryDateString);
    if (isNaN(expiry)) return null;
    const today = new Date();
    const timeDiff = expiry.getTime() - today.getTime();
    return Math.ceil(timeDiff / (1000 * 3600 * 24));
};

// --- FLEET SERVICE (CARS) ---
let fleetReadInFlight = null;   // the fleet read running now, shared by every caller (see getAllCars)
const copyCars = (rows) => (Array.isArray(rows) ? rows.map((c) => ({ ...c })) : rows);

/**
 * Car writes on the store (USE_CONTRACTS_V2), answered as fleetService answers: {success, count, conflicts,
 * notFound, error}. A car changed by someone else since it was read is not written (the others go through) and its
 * plate is named in `error`; `refresh`: the saved copy is read again afterwards, so the page shows what is stored.
 */
const storeFleetWrites = async (writes, what, { refresh = false } = {}) => {
    try {
        const out = await storeCarBatch(writes);
        const count = out.updated + out.unchanged;
        if (out.notFound.length) logger.warn(`[fleet] ${what}: ${out.notFound.length} car(s) not found, not written: ${out.notFound.slice(0, 10).map((n) => n.plate).join(', ')}`);
        const error = conflictText(out.conflicts);
        if (error) return { success: false, code: 'record-changed', error, count, conflicts: out.conflicts, notFound: out.notFound };
        return { success: true, count, conflicts: [], notFound: out.notFound };
    } catch (error) {
        logger.error(`[fleet] ${what} failed:`, error);
        return { success: false, error, code: error?.code };
    } finally {
        if (refresh) await fleetService.getAllCars(true, { ejar: false }).catch(() => {});
    }
};

export const fleetService = {
    /**
     * Every car. The saved copy is used while it is younger than FLEET_FRESH_MIN; older (up to
     * 24 h) only the cars changed since it was saved are read (`last_updated`, written by the
     * engine's hourly status / branch / KM merge and by this app) and merged in - so a status
     * or a renamed make shows within minutes, not after a day. Older than 24 h or forceRefresh:
     * the whole collection.
     *
     * Pages ask for the fleet from several places at the same moment (the page itself, its status
     * map, ...). One read now serves every caller that asks while it runs, instead of each running
     * its own "changed since" query and saving the copy again (audit FLT-21). Each caller gets its
     * own copies of the rows, so one page sorting or editing them cannot change another's.
     */
    // `throwOnError`: when neither Firestore nor the local copy can be read, throw instead of
    // returning [] - a caller that COUNTS cars (the Overview) must not show a failed read as
    // "0 cars" (audit ADM-15). Every other caller keeps the old empty-list fallback. The shared
    // read itself always throws, so a caller that joins it still gets the choice it asked for.
    // `ejar` (the contracts store, USE_CONTRACTS_V2): Ejar's status / branch / rate / KM from its vehicles laid over
    // the cars - false for a writer that plans against the cars as stored.
    getAllCars: (forceRefresh = false, { throwOnError = false, ejar = true } = {}) => {
        let run = !forceRefresh ? fleetReadInFlight : null;
        if (!run) {
            run = fleetService.readAllCars(forceRefresh);
            fleetReadInFlight = run;
            const shared = run;
            run.finally(() => { if (fleetReadInFlight === shared) fleetReadInFlight = null; }).catch(() => {});
        }
        const cars = run.then(copyCars, (error) => {
            if (throwOnError) throw error;
            return [];
        });
        // the store (USE_CONTRACTS_V2): Ejar's status / branch / rate / KM from its vehicles, over the fleet's own fields
        return USE_CONTRACTS_V2 && ejar ? cars.then((list) => (list.length ? withEjarVehicles(list, { force: forceRefresh }) : list)) : cars;
    },

    /** The read behind getAllCars (call getAllCars: it shares a read already running). Throws when nothing can be read. */
    readAllCars: async (forceRefresh = false) => {
        try {
            // the store (USE_CONTRACTS_V2): the saved copy + only the cars changed since its read (GET /api/store/fleet
            // ?since=, `removed` dropped first); each car carries `vehicle_id` and the `version` a save names
            if (USE_CONTRACTS_V2) return await storeCars({ force: forceRefresh });
            if (!forceRefresh) {
                const saved = await jsonCacheService.loadFromFile('fleet.json');
                const rows = saved && Array.isArray(saved.data) ? saved.data : null;
                const ageMin = saved && saved.timestamp ? (Date.now() - new Date(saved.timestamp).getTime()) / 60000 : Infinity;
                if (rows && rows.length && ageMin >= 0 && ageMin < FLEET_FRESH_MIN) return rows;
                if (rows && rows.length && ageMin >= 0 && fullAgeMin(saved) < 24 * 60) {
                    const since = new Date(new Date(saved.timestamp).getTime() - 5 * 60000).toISOString();   // 5 min overlap
                    const changed = await getDocs(query(fleetCollection, where('last_updated', '>=', since)));
                    const byPlate = new Map(rows.map((c) => [String(c.plate_number), c]));
                    changed.docs.forEach((d) => {
                        const car = d.data();
                        if (!car || car.moved_to) byPlate.delete(d.id);       // re-plated: its new doc comes in its own right
                        else byPlate.set(String(car.plate_number || d.id), car);
                    });
                    const merged = [...byPlate.values()];
                    await jsonCacheService.saveFleet(merged);
                    logger.info(`[fleet] ${changed.size} changed since ${since} merged into ${rows.length} saved cars`);
                    return merged;
                }
            }

            // 2. Fetch from DB - a renamed plate leaves a {moved_to} pointer behind: not a car
            const snapshot = await getDocs(fleetCollection);
            const data = snapshot.docs.map(doc => doc.data()).filter(car => car && !car.moved_to);

            // 3. Save to JSON Cache (a whole read: deleted / renamed cars are gone from it)
            await jsonCacheService.saveFleet(data, { full: true });

            return data;
        } catch (error) {
            logger.error('Error getting fleet cars:', error);
            // Fallback to JSON cache
            try {
                const cachedFleet = await jsonCacheService.loadFleet();
                if (cachedFleet) return cachedFleet;
            } catch (e) { }
            throw error;
        }
    },

    // The car behind a plate - also an OLD plate: a renamed car leaves {moved_to} at its
    // old id and lists it in previous_plates, so both roads lead to the current doc.
    getCarByPlate: async (plateNumber) => {
        if (!plateNumber) return null;
        // the store (USE_CONTRACTS_V2): the engine follows the plate history itself; none -> null
        if (USE_CONTRACTS_V2) return storeCarByPlate(plateNumber);
        let id = String(plateNumber);
        for (let hop = 0; hop < 4; hop++) {
            const snap = await getDoc(doc(fleetCollection, id));
            if (!snap.exists()) break;
            const data = snap.data();
            if (!data?.moved_to) return data;
            id = String(data.moved_to);
        }
        const q = query(fleetCollection, where('previous_plates', 'array-contains', String(plateNumber)), limit(1));
        const hits = await getDocs(q);
        const found = hits.docs.map(d => d.data()).find(d => d && !d.moved_to);
        return found || null;
    },

    // `version` (the store, USE_CONTRACTS_V2): the version of the car the page opened (its row); default the last
    // read. A car changed since is not written: {success: false, code: 'record-changed', error} and the fleet is
    // read again; a save answers {success: true, car} (the car as stored, with its new version).
    updateCar: async (plateNumber, updates, { version } = {}) => {
        if (USE_CONTRACTS_V2) {
            try {
                if (!plateNumber) throw new Error('Plate number is required');
                const car = await storeSaveCar(plateNumber, updates, version);
                await fleetService.getAllCars(true, { ejar: false }).catch(() => {});
                return { success: true, car };
            } catch (error) {
                logger.error('Error updating car:', error);
                if (error?.code === 'record-changed') await fleetService.getAllCars(true, { ejar: false }).catch(() => {});
                return { success: false, error: error.message, code: error?.code };
            }
        }
        try {
            if (!plateNumber) throw new Error('Plate number is required');
            const docRef = doc(fleetCollection, String(plateNumber));
            await setDoc(docRef, {
                ...cleanCarNames(updates),
                last_updated: new Date().toISOString()
            }, { merge: true });
            
            // Force refresh cache to persist inline edits across reloads
            await fleetService.getAllCars(true);
            
            return { success: true };
        } catch (error) {
            logger.error('Error updating car:', error);
            return { success: false, error: error.message };
        }
    },

    // Fleet Manager's bulk edits ("Clear to Yelo", "Set company"): merge each car's fields. Updates only - the
    // archive (a car marked sold) and add (a new car) writes served the Fleet file / RTA master list imports, which
    // went with the manual uploads (owner, 28 Sep 2026; review, 28 Sep 2026): a car leaves the fleet by Ejar's status
    // or the RTA reconcile and is created by the engine's RTA licence tool, never by a page.
    executeMasterSync: async (updates) => {
        const CHUNK_SIZE = 450;
        const operations = (updates || []).map((car) => ({ type: 'UPDATE', data: car }));

        if (operations.length === 0) return { success: true, count: 0 };

        // the store (USE_CONTRACTS_V2): POST /api/store/fleet/batch - each update on the version its car was read with
        // (the page's, else the last read); the fleet is read again afterwards so Fleet Manager sees the result at once
        if (USE_CONTRACTS_V2) return storeFleetWrites(masterSyncWrites(updates), 'Master Sync', { refresh: true });

        try {
            const chunks = [];
            for (let i = 0; i < operations.length; i += CHUNK_SIZE) {
                chunks.push(operations.slice(i, i + CHUNK_SIZE));
            }

            let processed = 0;
            for (let ci = 0; ci < chunks.length; ci++) {
                if (ci > 0) await sleep(BATCH_DELAY_MS);
                const chunk = chunks[ci];
                const batch = writeBatch(db);
                chunk.forEach(op => {
                    const plate = String(op.data.plate_number || op.data.plate);
                    batch.set(doc(fleetCollection, plate), {
                        ...cleanCarNames(op.data),
                        last_updated: new Date().toISOString()
                    }, { merge: true });
                });
                await batch.commit();
                processed += chunk.length;
            }

            // --- CACHE REFRESH FIX ---
            // Force refresh of JSON cache so Fleet Manager sees updates immediately
            await fleetService.getAllCars(true);
            logger.info('Master Sync: Cache refreshed successfully.');

            return { success: true, count: processed };
        } catch (error) {
            logger.error('Error executing master sync:', error);
            return { success: false, error };
        }
    }
};

// --- CONTRACTS SERVICE ---
const CONTRACTS_FRESH_MIN = 15;   // a saved contracts copy younger than this is used as it is
const FLEET_FRESH_MIN = 10;
const MAINTENANCE_FRESH_MIN = 10; // the engine closes finished logs hourly: a copy older than this asks what changed
const AIRPORT_FRESH_MIN = 15;     // the same for the airport contracts (engine: hourly); the fleet: the engine merges statuses hourly
/** Minutes since the saved copy was last read WHOLE (a merge of the changed rows cannot see a deletion); a copy
 *  saved before fullReadAt existed counts as never read whole, so it is read whole once. */
const fullAgeMin = (saved) => (saved && saved.fullReadAt ? (Date.now() - new Date(saved.fullReadAt).getTime()) / 60000 : Infinity);

/** Deleted contracts leave the saved copy at once (a read of what changed cannot see a deletion). */
const dropFromContractsCache = async (ids) => {
    const gone = new Set((ids || []).map((x) => String(x)));
    const saved = await jsonCacheService.loadFromFile('contracts.json');
    if (!saved || !Array.isArray(saved.data) || !gone.size) return;
    const kept = saved.data.filter((r) => !gone.has(String(r.id)));
    if (kept.length !== saved.data.length) {
        await jsonCacheService.saveToFile('contracts.json', kept);
        await jsonCacheService.markStale('contracts.json', 16 + (Date.now() - new Date(saved.timestamp).getTime()) / 60000);   // never younger than it was: the next read still refreshes
    }
};

/** One contracts document -> the row every page uses (Ejar headers + calculator fields). */
const contractRowOf = (doc) => {
    const docData = doc.data();
    return {
        id: doc.id,
        'Contract No.': docData.contract_no,
        'Booking Number': docData.booking_number,
        'Customer': docData.customer,
        'Pick-up Branch': docData.pickup_branch,
        'Plate No.': docData.plate_no,
        'Model': docData.model,
        'Pick-up Date': docData.pickup_date,
        'Drop-off Date': docData.dropoff_date,
        'Phone Number': cleanPhoneNumber(docData.phone),
        'Status': docData.status,
        'Invygo Plate': docData.invygo_plate || '',
        'Invygo Model': docData.invygo_model || '',
        'Created By': docData.created_by || '',
        
        // Fields for Monthly Calculator & Filtering
        contract_no: docData.contract_no,
        booking_number: docData.booking_number,
        // who opened it on Ejar and the booking it came from (owner, 5 Oct 2026)
        created_by: docData.created_by || '',
        booking_source: docData.booking_source || '',
        booking_by: docData.booking_by || '',
        opened_by_short: docData.opened_by_short || '',
        customer: docData.customer,
        phone: cleanPhoneNumber(docData.phone),
        model: docData.model,
        plate_no: docData.plate_no,
        status: docData.status,
        invygo_plate: docData.invygo_plate || '',
        invygo_model: docData.invygo_model || '',
        // Invygo fingerprint bookkeeping written by the fines engine
        invygo_fingerprint_source: docData.invygo_fingerprint_source || '',
        car_history: Array.isArray(docData.car_history) ? docData.car_history : [],
        on_yelo_car: !!docData.on_yelo_car,
        // when the contract's car now came, when it is not the car on Ejar's contract page (contractsV2.carNowOf)
        car_now_at: docData.car_now_at || '',
        // the car it was opened with (any contract): the Monthly table's own car when it has no Invygo car
        opened_plate: docData.opened_plate || '',
        opened_model: docData.opened_model || '',
        // Rent facts (Monthly Calculator): the rate schedule saved by the
        // calculator, and what the engine learned from Ejar (opening comment)
        rate_schedule: Array.isArray(docData.rate_schedule) ? docData.rate_schedule : [],
        rent_source: docData.rent_source || '',
        rent_conflict: docData.rent_conflict || '',
        ejar_opening_comment: docData.ejar_opening_comment || '',
        rental_rate: docData.rental_rate ?? '',
        // total_rent was never mapped here, so a rent saved on the document
        // (calculator Save, or learned from Ejar) never reached the page.
        total_rent: docData.total_rent ?? '',
        contract_type: docData.contract_type || '', // Critical for filtering
        
        // Billing Info. updateContract has always WRITTEN these snake_case (invoice_company_name ...),
        // but this row read only the camelCase keys, so a company, TRN, KM limit or pick-up KM saved
        // in the calculator was gone on the next reload (CON-02). snake_case first; the camelCase
        // keys of older documents are still read.
        invoiceCompanyName: docData.invoice_company_name || docData.invoiceCompanyName || '',
        invoiceCompanyAddress: docData.invoice_company_address || docData.invoiceCompanyAddress || '',
        invoiceCompanyTRN: docData.invoice_company_trn || docData.invoiceCompanyTRN || '',
        invoiceKMLimit: docData.invoice_km_limit || docData.invoiceKMLimit || 2500,
        pickupKM: docData.pickup_km || docData.pickupKM || 0,
        
        comments: docData.comments || [],
        addons: docData.addons || {},
        adminComment: docData.adminComment || '',
        monthly_calculations: docData.monthly_calculations || [],
        netBalance: docData.net_balance || 0,
        net_balance: docData.net_balance || 0,
        updateHistory: docData.update_history || [],
        updatedBy: docData.updated_by ? `${docData.updated_by}${docData.update_source ? ` (${docData.update_source})` : ''}` : '',
        _last_updated: docData.last_updated
    };
};

export const contractsService = {
    /**
     * Every Business Bay / Invygo contract row. The saved copy (IndexedDB) is used while it is
     * younger than CONTRACTS_FRESH_MIN; older than that (up to 24 h) only the contracts changed since
     * it was saved are read (`last_updated`, an ISO text written by the engine and by this app) and
     * merged in - tens of reads instead of the whole collection (~11k). forceRefresh (the Refresh
     * buttons, the reload after a Fill from Ejar or a delete) skips the fresh window but still reads
     * only what changed: it used to read the whole collection per click, per user (CON-19). Deleted
     * contracts leave the copy at once (dropFromContractsCache). Older than 24 h: the whole collection.
     */
    getAllContracts: async (forceRefresh = false, { since } = {}) => {
        // the store: the OPEN contracts - with `since` ("yyyy-mm-dd", Dubai) also the ones returned since that day,
        // for a page that shows a period (never the whole five years)
        if (USE_CONTRACTS_V2) {
            if (forceRefresh) forgetClosedReads();
            return (await (since ? v2MonthlyDocsSince(since) : v2BusinessBayDocs())).map(contractRowOf);
        }
        try {
            const saved = await jsonCacheService.loadFromFile('contracts.json');
            const rows = saved && Array.isArray(saved.data) ? saved.data : null;
            const ageMin = saved && saved.timestamp ? (Date.now() - new Date(saved.timestamp).getTime()) / 60000 : Infinity;
            if (!forceRefresh && rows && rows.length && ageMin >= 0 && ageMin < CONTRACTS_FRESH_MIN) return rows;
            if (rows && rows.length && ageMin >= 0 && fullAgeMin(saved) < 24 * 60) {
                const since = new Date(new Date(saved.timestamp).getTime() - 5 * 60000).toISOString();   // 5 min overlap
                const changed = await getDocs(query(contractsCollection, where('last_updated', '>=', since)));
                const byId = new Map(rows.map((r) => [r.id, r]));
                changed.docs.forEach((d) => byId.set(d.id, contractRowOf(d)));
                const merged = [...byId.values()];
                await jsonCacheService.saveContracts(merged);
                logger.info(`[contracts] ${changed.size} changed since ${since} merged into ${rows.length} saved rows`);
                return merged;
            }

            // 2. The whole collection
            const snapshot = await getDocs(contractsCollection);
            const data = snapshot.docs.map(contractRowOf);

            // 3. Save to JSON Cache (a whole read: deleted contracts are gone from it)
            await jsonCacheService.saveContracts(data, { full: true });

            return data;

        } catch (error) {
            logger.error('Error getting all contracts:', error);

            // Fallback to JSON cache even if expired
            try {
                const cachedContracts = await jsonCacheService.loadContracts();
                if (cachedContracts && cachedContracts.length > 0) {
                    return cachedContracts;
                }
            } catch (e) { }
            // No copy at all: the error goes to the page. An empty list looked like "0 contracts" (CON-13).
            throw error;
        }
    },

    /**
     * The open Business Bay + Invygo contracts (the new store; owner, 27 Sep 2026: "لازم الصفحات تكون سريعة").
     * Like the live page's contracts read: a copy kept in the browser is shown AT ONCE (any age under 24 h), then
     * only the contracts changed since that copy (last_updated) are read in the background and merged - a contract
     * that closed leaves the list - and `onUpdate(rows)` gets the fresh list. With no copy (or an old one, or
     * `force`), the open contracts are read whole and kept.
     * "Since that copy" is the ENGINE's clock: the copy keeps the `asOf` of the read it is up to date with, and the
     * next read starts 2 min before it (changesSinceOf) - the browser's clock only for an engine that sends no asOf.
     */
    getOpenContracts: async ({ force = false, onUpdate } = {}) => {
        const FILE = 'contracts_v2_open.json';
        // kept as { rows, asOf } (a copy saved before 27 Sep 2026 is the bare rows: no asOf)
        const save = (rows, asOf, opts) => jsonCacheService.saveToFile(FILE, { rows, asOf: asOf || '' }, opts);
        const readWhole = async () => {
            const { docs, asOf } = await v2MonthlyOpenWithAsOf();
            const data = docs.map(contractRowOf);
            await save(data, asOf, { full: true });
            return data;
        };
        const saved = force ? null : await jsonCacheService.loadFromFile(FILE);
        const kept = saved ? saved.data : null;
        const rows = Array.isArray(kept) ? kept : (kept && Array.isArray(kept.rows) ? kept.rows : null);
        if (!rows || !rows.length || !(fullAgeMin(saved) < 24 * 60)) return readWhole();
        // a copy saved before the contract's source and short "Opened By" name were sent (5 Oct 2026) is read again
        // whole: an unchanged contract is never in the delta, so they would stay unknown up to a day
        if (rows.some((r) => !('opened_by_short' in r))) return readWhole();
        // ... and one saved before the car each contract has NOW was sent (4 Nov 2026: a swapped contract showed its
        // first car, so Mismatch / Switch Back stayed at 0)
        if (rows.some((r) => !('car_now_at' in r))) return readWhole();
        // the contracts changed since the copy are ALWAYS read (in the background - one light call): a copy younger than
        // 15 min was shown as it was, so a balance changed meanwhile took several reloads to appear (owner, 4 Oct 2026:
        // "لازم اعمل ريفريش 3 او 4 مرات علشان الرقم يتظبط")
        const since = changesSinceOf(kept.asOf, saved.timestamp);
        if (!since) return readWhole();
        (async () => {
            try {
                const byId = new Map(rows.map((r) => [r.id, r]));
                const { docs: changed, asOf } = await v2MonthlyChangedSinceWithAsOf(since);
                if (!changed.length) {                    // nothing new: the copy stays, only its asOf moves on
                    const current = await jsonCacheService.loadFromFile(FILE);
                    if (!current || current.timestamp === saved.timestamp) await save(rows, asOf);
                    return;
                }
                changed.forEach((d) => {
                    if (String(d.data().status || '').trim().toLowerCase() === 'open') byId.set(d.id, contractRowOf(d));
                    else byId.delete(d.id);
                });
                const merged = [...byId.values()];
                // A read that began after this one (a Refresh) may have saved its copy meanwhile: this older merge
                // does not replace it, or the next visit would show the older list first.
                const current = await jsonCacheService.loadFromFile(FILE);
                if (current && current.timestamp !== saved.timestamp) logger.info('[contracts v2] a newer copy was saved while the changes were read - it is kept');
                else await save(merged, asOf);
                logger.info(`[contracts v2] ${changed.length} changed since ${since}${asOf ? '' : ' (browser clock: no asOf from the engine)'} merged into ${rows.length} saved open rows`);
                if (onUpdate) onUpdate(merged);
            } catch (e) {
                logger.warn('[contracts v2] background refresh failed - the saved copy stays on screen', e);
            }
        })();
        return rows;
    },

    /** Business Bay + Invygo contracts, open or closed, found by what staff typed (the new store's search keys). */
    findMonthlyContracts: async (text) => (await v2MonthlySearch(text)).map(contractRowOf),

    /**
     * One contract as it is on the server NOW (one read), as the same row getAllContracts gives;
     * null when it does not exist. The calculator opens from it, not from the page's copy, which
     * does not refresh while the page stays open (CON-03).
     */
    getContractRow: async (id) => {
        if (USE_CONTRACTS_V2) {
            // the one contract the calculator opens also brings Ejar's comments (owner, 4 Oct 2026) - only here, so the
            // list copy kept in the browser stays light
            const [doc] = await v2MonthlyByIds([id]);
            if (!doc) return null;
            const comments = doc.data().ejar_comments;
            return { ...contractRowOf(doc), ejar_comments: Array.isArray(comments) ? comments : [] };
        }
        const snap = await getDocFromServer(doc(contractsCollection, String(id)));
        return snap.exists() ? contractRowOf(snap) : null;
    },

    /** Contract rows by contract number (doc id), 30 per query - for pages that need a few, not all ~11k. */
    getContractRowsByIds: async (contractIds) => {
        if (USE_CONTRACTS_V2) return (await v2MonthlyByIds(contractIds)).map(contractRowOf);
        const ids = [...new Set((contractIds || []).map((c) => String(c || '').trim().toUpperCase()).filter(Boolean))];
        const out = [];
        for (let i = 0; i < ids.length; i += 30) {
            const snap = await getDocs(query(contractsCollection, where(documentId(), 'in', ids.slice(i, i + 30))));
            snap.docs.forEach((d) => out.push(contractRowOf(d)));
        }
        return out;
    },

    /**
     * Save UI fields onto a contract document (merge) with an update_history entry.
     * `expectedLastUpdated` (the `last_updated` of the copy the caller edited): the save runs in a
     * transaction and is REFUSED (Error code 'contract-changed') when the document was written after
     * that copy was read - so a calculator opened before a Fill from Ejar cannot write old month rows
     * over the engine's (CON-03). Leave it out for field-only saves (they never send month rows).
     */
    updateContract: async (id, updates, updatedBy = null, updateSource = null, { expectedLastUpdated } = {}) => {
        // Convert UI keys back to Firestore keys
        const firestoreUpdates = {};

        // Helper to get value from either Title Case key or camelCase key
        const getVal = (titleKey, camelKey) => {
            if (updates[camelKey] !== undefined) return updates[camelKey];
            if (updates[titleKey] !== undefined) return updates[titleKey];
            return undefined;
        };

        const contractNo = getVal('Contract No.', 'contractNo');
        if (contractNo !== undefined) firestoreUpdates.contract_no = contractNo.toString().toUpperCase();

        const bookingNumber = getVal('Booking Number', 'bookingNumber');
        if (bookingNumber !== undefined) firestoreUpdates.booking_number = bookingNumber;

        const customer = getVal('Customer', 'customer');
        if (customer !== undefined) firestoreUpdates.customer = customer;

        const pickupBranch = getVal('Pick-up Branch', 'pickupBranch');
        if (pickupBranch !== undefined) firestoreUpdates.pickup_branch = pickupBranch;

        const plateNo = getVal('Plate No.', 'plateNo') || getVal('Plate No.', 'ejarPlate');
        if (plateNo !== undefined) firestoreUpdates.plate_no = plateNo;

        const model = getVal('Model', 'model') || getVal('Model', 'ejarModel');
        if (model !== undefined) firestoreUpdates.model = model;

        const pickupDate = getVal('Pick-up Date', 'pickupDate');
        if (pickupDate !== undefined) firestoreUpdates.pickup_date = pickupDate;

        const dropoffDate = getVal('Drop-off Date', 'dropoffDate');
        if (dropoffDate !== undefined) firestoreUpdates.dropoff_date = dropoffDate;

        const phone = getVal('Phone Number', 'phoneNumber');
        if (phone !== undefined) {
            firestoreUpdates.phone = phone ? cleanPhoneNumber(phone) : ""; // Allow clearing phone
        }

        const status = getVal('Status', 'status');
        if (status !== undefined) firestoreUpdates.status = status;

        const invygoPlate = getVal('Invygo Plate', 'invygoPlate');
        if (invygoPlate !== undefined) firestoreUpdates.invygo_plate = invygoPlate;
        // A hand-typed Invygo plate (Edit Mode) is the fingerprint the engine must keep:
        // the caller flags the change so the source is recorded as manual.
        if (updates._invygoPlateChanged && invygoPlate) {
            firestoreUpdates.invygo_fingerprint_source = 'manual';
            firestoreUpdates.invygo_fingerprint_at = new Date().toISOString().slice(0, 19).replace('T', ' ');
        }

        const invygoModel = getVal('Invygo Model', 'invygoModel');
        if (invygoModel !== undefined) firestoreUpdates.invygo_model = invygoModel;

        const monthlyCalculations = getVal('monthly_calculations', 'monthly_calculations');
        if (monthlyCalculations !== undefined) firestoreUpdates.monthly_calculations = monthlyCalculations;

        // The calculator's corrected monthly rate and its rate schedule: they were sent but never
        // mapped here, so a rate typed in the calculator was lost on the next reload (CON-02).
        const totalRent = getVal('Total Rent', 'total_rent');
        if (totalRent !== undefined) firestoreUpdates.total_rent = totalRent;

        const rateSchedule = getVal('rate_schedule', 'rate_schedule');
        if (rateSchedule !== undefined) firestoreUpdates.rate_schedule = rateSchedule;

        const invoiceCompanyName = getVal('invoice_company_name', 'invoiceCompanyName');
        if (invoiceCompanyName !== undefined) firestoreUpdates.invoice_company_name = invoiceCompanyName;

        const invoiceCompanyAddress = getVal('invoice_company_address', 'invoiceCompanyAddress');
        if (invoiceCompanyAddress !== undefined) firestoreUpdates.invoice_company_address = invoiceCompanyAddress;

        const invoiceCompanyTRN = getVal('invoice_company_trn', 'invoiceCompanyTRN');
        if (invoiceCompanyTRN !== undefined) firestoreUpdates.invoice_company_trn = invoiceCompanyTRN;

        const invoiceKMLimit = getVal('invoice_km_limit', 'invoiceKMLimit');
        if (invoiceKMLimit !== undefined) firestoreUpdates.invoice_km_limit = invoiceKMLimit;

        const pickupKMValue = getVal('pickup_km', 'pickupKM');
        if (pickupKMValue !== undefined) firestoreUpdates.pickup_km = pickupKMValue;

        const commentsVal = getVal('comments', 'comments');
        if (commentsVal !== undefined) firestoreUpdates.comments = commentsVal;

        const adminCommentVal = getVal('admin_comment', 'adminComment');
        if (adminCommentVal !== undefined) firestoreUpdates.adminComment = adminCommentVal;

        const addonsVal = getVal('addons', 'addons');
        if (addonsVal !== undefined) firestoreUpdates.addons = addonsVal;

        const docRef = doc(contractsCollection, id.toString());
        // --- ENRICHMENT: History & Net Balance (from the document as it is now) ---
        const enrich = (currentData) => {
          try {

            // 1. Check for explicit net_balance update (e.g. from Monthly Calculator)
            if (updates.netBalance !== undefined) firestoreUpdates.net_balance = parseFloat(updates.netBalance);
            if (updates.net_balance !== undefined) firestoreUpdates.net_balance = parseFloat(updates.net_balance);
            
            // Note: We bypass strict backend recalculation as requested by the user, trusting the UI payload.
            // This prevents complex 0-balance overwrite bugs when saving from other modal views.

            // 2. Create History Entry
            const nameOnly = updatedBy ? (updatedBy.includes('@') ? updatedBy.split('@')[0] : updatedBy) : 'Unknown';
            
            // Only log fields that ACTUALLY changed
            const changes = [];
            Object.keys(firestoreUpdates).forEach(key => {
                if (['last_updated', 'updated_by', 'update_source', 'update_history', 'net_balance'].includes(key)) return;
                
                const newVal = firestoreUpdates[key];
                const oldVal = currentData[key];
                
                // Always push 'monthly_calculations' to changes if it exists, so the deep comparator runs
                if (key === 'monthly_calculations') {
                    changes.push(key);
                } else if (newVal !== oldVal && !(typeof newVal === 'object' && JSON.stringify(newVal) === JSON.stringify(oldVal))) {
                    // (an unchanged list - comments, add-ons, rate schedule - is not a change)
                    changes.push(key);
                }
            });

            if (changes.length > 0) {
                const descriptiveChanges = [];
                
                changes.forEach(key => {
                    const newVal = firestoreUpdates[key];
                    const oldVal = currentData[key];

                    if (key === 'monthly_calculations') {
                        const oldList = oldVal || [];
                        const newList = newVal || [];

                        // 1. Detect Changes/Deletions
                        oldList.forEach(oldRow => {
                            const newRow = newList.find(nr => nr.start === oldRow.start && nr.end === oldRow.end);
                            const period = `${oldRow.start || '?'}`;

                            if (!newRow) {
                                descriptiveChanges.push(`Removed Month: ${period}`);
                            } else {
                                // Check fields within row
                                const fieldsToTrack = [
                                    'start', 'end', 'rent', 'vat', 'totalRent', 'salik', 'fine', 'parking', 
                                    'other', 'paid', 'totalAmount', 'invNo', 'Balance', 'startKM', 'currentKM', 'date', 'dashboardImage'
                                ];
                                fieldsToTrack.forEach(f => {
                                    // Normalize values for comparison
                                    let nv = newRow[f];
                                    let ov = oldRow[f];
                                    
                                    const stringFields = ['start', 'end', 'invNo', 'date', 'dashboardImage'];
                                    if (!stringFields.includes(f)) {
                                        nv = parseFloat(nv) || 0;
                                        ov = parseFloat(ov) || 0;
                                    } else {
                                        nv = nv || '';
                                        ov = ov || '';
                                    }

                                    if (nv !== ov) {
                                        const label = f.replace(/([A-Z])/g, ' $1').trim().replace(/^\w/, c => c.toUpperCase());
                                        descriptiveChanges.push(`${label} (${period}): ${ov || (stringFields.includes(f) ? 'None' : 0)} → ${nv || (stringFields.includes(f) ? 'None' : 0)}`);
                                    }
                                });
                            }
                        });

                        // 2. Detect Additions
                        newList.forEach(newRow => {
                            const exists = oldList.find(or => or.start === newRow.start && or.end === newRow.end);
                            if (!exists) {
                                descriptiveChanges.push(`Added Month: ${newRow.start || '?'}`);
                            }
                        });
                    } else {
                        const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                        // a rate schedule reads "01/09/2026: 2400, 01/10/2026: 2200" in the history, not [object Object]
                        const show = (v) => (key === 'rate_schedule' && Array.isArray(v)
                            ? (v.map((e) => `${e && e.from}: ${e && e.rate}`).join(', ') || 'None') : (v || 'None'));
                        descriptiveChanges.push(`${label}: ${show(oldVal)} → ${show(newVal)}`);
                    }
                });

                if (descriptiveChanges.length > 0) {
                    const historyEntry = {
                        timestamp: new Date().toISOString(),
                        user: nameOnly,
                        source: updateSource || 'Direct',
                        changes: descriptiveChanges
                    };

                    // 3. Update History Array (Limit to last 50 entries)
                    const oldHistory = currentData.update_history || [];
                    firestoreUpdates.update_history = [historyEntry, ...oldHistory].slice(0, 50);
                }
            }

          } catch (e) {
            logger.error("Error enriching update with history:", e);
          }
          firestoreUpdates.last_updated = new Date().toISOString();
          if (updatedBy) {
              const nameOnly = updatedBy.includes('@') ? updatedBy.split('@')[0] : updatedBy;
              firestoreUpdates.updated_by = nameOnly;
          }
          if (updateSource) firestoreUpdates.update_source = updateSource;
        };

        if (expectedLastUpdated !== undefined) {
            // Read-check-write in one transaction: nothing can land between the check and the write.
            await runTransaction(db, async (tx) => {
                const snap = await tx.get(docRef);
                const currentData = snap.exists() ? snap.data() : {};
                if (contractChangedSince(currentData, expectedLastUpdated)) throw contractChangedError(currentData);
                enrich(currentData);
                tx.set(docRef, firestoreUpdates, { merge: true });
            });
        } else {
            let currentData = {};
            try {
                const docSnap = await getDoc(docRef);
                currentData = docSnap.exists() ? docSnap.data() : {};
            } catch (e) {
                logger.error("Error reading the contract for its history:", e);
            }
            enrich(currentData);
            await setDoc(docRef, firestoreUpdates, { merge: true });
        }
        
        // The saved copy is stale now: the next read fetches what changed (this contract included)
        await jsonCacheService.markStale('contracts.json');
        
        return firestoreUpdates;
    },

    // Fuzzy Search for Smart Manual Entry (Active + Archive)
    searchContracts: async (term) => {
        if (!term) return [];
        try {
            // const results = [];
            const termStr = term.toString().trim();
            const termUpper = termStr.toUpperCase();

            // --- 1. Active Contracts Search ---
            const activePromise = (async () => {
                const activeResults = [];
                try {
                    // Invygo's own booking id typed (441738): the contracts of that booking, when the term named
                    // no contract by itself - the Invygo sheet's upload used to write that id into
                    // contracts.booking_number; with the sheet gone (owner, 28 Sep 2026) Ejar's booking -> broker
                    // reference names them (src/services/invygoIds.js). A failed lookup finds nothing more.
                    const invygoContracts = async () => {
                        try {
                            return await contractsForInvygoId(termStr);
                        } catch (e) {
                            logger.warn('[contracts] Invygo id lookup failed:', e?.message || e);
                            return [];
                        }
                    };
                    // The store: the Business Bay / Invygo contracts whose number or booking number IS the term
                    if (USE_CONTRACTS_V2) {
                        let found = await v2ExactRecords(termStr, 'monthly');
                        if (found.length === 0) {
                            const nos = await invygoContracts();
                            if (nos.length > 0) found = await v2RecordsByIds(nos, 'monthly');
                        }
                        found.forEach((v) => activeResults.push({ id: v.contract_no, ...toLegacyContract(v) }));
                        return activeResults;
                    }
                    // A. Direct Contract No Match (ID)
                    const docRef = doc(contractsCollection, termUpper);
                    const docSnap = await getDoc(docRef);
                    if (docSnap.exists()) {
                        activeResults.push({ id: docSnap.id, ...docSnap.data() });
                    }

                    // B. Booking Number (String & Number)
                    const searchValues = [termStr];
                    const numVal = Number(termStr);
                    if (!isNaN(numVal) && termStr !== '' && Number.isSafeInteger(numVal)) {
                        searchValues.push(numVal);
                    }

                    const q = query(contractsCollection, where('booking_number', 'in', searchValues));
                    const snapshot = await getDocs(q);

                    snapshot.forEach(d => {
                        if (!activeResults.some(r => r.id === d.id)) {
                            activeResults.push({ id: d.id, ...d.data() });
                        }
                    });

                    // C. An Invygo booking id typed: its contracts (see invygoContracts above)
                    if (activeResults.length === 0) {
                        for (const no of await invygoContracts()) {
                            const snap = await getDoc(doc(contractsCollection, no));
                            if (snap.exists()) activeResults.push({ id: snap.id, ...snap.data() });
                        }
                    }
                } catch (e) {
                    logger.error("Active search error:", e);
                }
                return activeResults;
            })();

            // --- 2. Archive Search ---
            const archivePromise = (async () => {
                // const archiveResults = [];
                try {
                    // Search by Booking Number & Contract Number
                    const [byBooking, byContract] = await Promise.all([
                        archiveService.findByBookingNumber(termStr),
                        archiveService.findByContractNumbers([termStr])
                    ]);


                    return [...byBooking, ...byContract];
                } catch (e) {
                    logger.error("Archive search error:", e);
                    return [];
                }
            })();

            // --- 3. Execute & Merge ---
            const [activeData, archiveData] = await Promise.all([activePromise, archivePromise]);


            // Map Active
            const mappedActive = activeData.map(data => ({
                id: data.id,
                source: 'active',
                contract_no: data.contract_no || data.id,
                booking_number: data.booking_number,
                customer: data.customer,
                plate_number: data.plate_no || data.plate_number,
                model: data.model,
                pickup_date: data.pickup_date,
                dropoff_date: data.dropoff_date,
                phone: data.phone,
                status: data.status || 'Active',
            }));

            // Map Archive
            const mappedArchive = archiveData.map(item => {
                // IMPORTANT: In Archive, one contract number might appear multiple times if the car was swapped or data is duplicated?
                // Or maybe distinct records share the same Contract No but different plates?
                // We should ensure ID is unique if we want to show all of them.
                const cNo = item['Contract No.'] || item['Contract No'] || item['Agreement'] || `ARCH_${Math.random()}`;
                // Usage of random ID suffix to ensure uniqueness if multiple archive rows have same contract No.
                // If they are truly different rows (e.g. different cars), they should be kept.
                const uniqueId = `${cNo}_${Math.floor(Math.random() * 10000)}`;

                return {
                    id: uniqueId,
                    source: 'archive',
                    contract_no: cNo,
                    booking_number: item['Booking Number'] || item['Booking No'] || item['Booking ID'],
                    customer: item['Customer Name'] || item['Customer'],
                    plate_number: item['Plate Number'] || item['Plate No.'] || item['Plate No'],
                    model: item['Model'],
                    pickup_date: item['Pick-up Date'] || item['Transaction Date'],
                    dropoff_date: item['Drop-off Date'] || item['Return Date'],
                    phone: item['Phone Number'] || item['Mobile Number'],
                    status: 'Closed (Archive)',
                    original_data: item
                };
            });

            // Combine & Deduplicate
            const combined = [...mappedActive];
            // CHANGE: Do NOT deduplicate by Contract Number strictly if they have different Plate Numbers.
            // Only deduplicate if it's the EXACT same record.
            // Since we can't easily tell, let's allow duplicates from Archive unless Plate & Contract match Active.

            mappedArchive.forEach(archItem => {
                // Check if this specific Plate + Contract combo exists in Active
                const existsInActive = combined.some(active =>
                    active.contract_no === archItem.contract_no &&
                    active.plate_number === archItem.plate_number
                );

                if (!existsInActive) {
                    combined.push(archItem);
                }
            });

            // --- 4. DATA ENRICHMENT (Backfill missing details from peers) ---
            const groups = {};
            combined.forEach(item => {
                const cKey = item.contract_no ? item.contract_no.toString().trim().toUpperCase() : null;
                if (cKey) {
                    if (!groups[cKey]) groups[cKey] = [];
                    groups[cKey].push(item);
                }
            });

            Object.values(groups).forEach(group => {
                if (group.length > 1) {
                    const bestCustomer = group.find(i => i.customer && i.customer !== 'Customer Name' && i.customer.trim().length > 0)?.customer;
                    const bestBooking = group.find(i => i.booking_number && i.booking_number !== 'Booking ID' && i.booking_number.toString().trim().length > 0)?.booking_number;
                    const bestPhone = group.find(i => i.phone && i.phone.toString().trim().length > 0)?.phone;

                    group.forEach(item => {
                        if ((!item.customer || item.customer === 'Customer Name') && bestCustomer) item.customer = bestCustomer;
                        if ((!item.booking_number || item.booking_number === 'Booking ID') && bestBooking) item.booking_number = bestBooking;
                        if (!item.phone && bestPhone) item.phone = bestPhone;
                    });
                }
            });

            return combined;

        } catch (error) {
            logger.error("Error searching contracts (Active+Archive):", error);
            return [];
        }
    },

    deleteContract: async (id) => {
        try {
            const docRef = doc(contractsCollection, id);
            await deleteDoc(docRef);
            await dropFromContractsCache([id]);
            return { success: true };
        } catch (error) {
            logger.error("Error deleting contract:", error);
            return { success: false, error };
        }
    },

    getContractByBookingNumber: async (bookingNumber) => {
        try {
            // Handle both String and Number types in the database
            const searchValues = [bookingNumber.toString()];
            const numVal = Number(bookingNumber);
            if (!isNaN(numVal) && bookingNumber.trim() !== '' && Number.isSafeInteger(numVal)) {
                searchValues.push(numVal);
            }

            const q = query(contractsCollection, where('booking_number', 'in', searchValues));
            const snapshot = await getDocs(q);

            if (snapshot.empty) return null;

            // Return the LATEST match (Sort by Pickup Date descending)
            // Client-side sort is safer as 'pickup_date' format varies (String date vs ISO)
            const sortedDocs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
                .sort((a, b) => {
                    // Try parsing dates for robust sorting
                    const dateA = isValid(parse(a.pickup_date, 'dd/MM/yyyy HH:mm', new Date()))
                        ? parse(a.pickup_date, 'dd/MM/yyyy HH:mm', new Date())
                        : (new Date(a.pickup_date || 0));
                    const dateB = isValid(parse(b.pickup_date, 'dd/MM/yyyy HH:mm', new Date()))
                        ? parse(b.pickup_date, 'dd/MM/yyyy HH:mm', new Date())
                        : (new Date(b.pickup_date || 0));
                    return dateB - dateA; // Descending
                });

            const data = sortedDocs[0];

            return {
                id: data.id,
                'Contract No.': data.contract_no,
                'Booking Number': data.booking_number,
                'Customer': data.customer,
                'Pick-up Branch': data.pickup_branch,
                'Plate No.': data.plate_no,
                'Model': data.model,
                'Pick-up Date': data.pickup_date,
                'Drop-off Date': data.dropoff_date,
                'Phone Number': data.phone,
                'Status': data.status,
                'Invygo Plate': data.invygo_plate || '',
                'Invygo Model': data.invygo_model || '',

                // Mappings specific for MileageCalculator requirements
                'Close Date': data.dropoff_date || data.close_date || '',
                'Car Model': data.model,
                'Drop-Off Dte': data.dropoff_date // Ensure compatibility with closed logic
            };
        } catch (error) {
            logger.error('Error fetching contract by booking number:', error);
            return null;
        }
    },

    getContractsByBookingNumber: async (searchValue) => {
        try {
            const term = searchValue.toString().trim();
            const termUpper = term.toUpperCase();

            const upper = (v) => cleanText(v).toUpperCase();

            // Handle both String and Number types for booking_number
            const searchValues = [term];
            const numVal = Number(term);
            if (!isNaN(numVal) && term !== '' && Number.isSafeInteger(numVal)) {
                searchValues.push(numVal);
            }

            // --- DEEP ENRICHMENT (Invygo & Airport) ---
            let invygoCustomer = null;
            let airportMatchData = null;
            let airportMatches = [];

            try {
                // 1. Check the old Invygo sheet (history; flags off only - with the store every contract carries its
                //    own customer)
                if (!USE_CONTRACTS_V2) {
                    const invygoData = await invygoService.getInvygoDataByIdentifier(term);
                    if (invygoData && invygoData.customer_name) {
                        invygoCustomer = invygoData.customer_name;
                    }
                }

                // 2. Check Airport Database (The missing piece for consistency with History Lens)
                airportMatches = (await airportService.getAirportContractsByIdentifier(term)) || [];
                if (airportMatches.length > 0) {
                    airportMatchData = airportMatches[0];
                }
            } catch (e) {
            }
            const airportCustomer = () => airportMatchData?.Customer || airportMatchData?.['Customer Name'] || null;
            // --- ENRICHMENT END ---

            const contracts = [];
            let invygoNos = [];   // flags off: the contracts of an Invygo booking id typed (D below)
            const mapFirestoreDoc = (id, data) => {
                const baseCustomer = data.customer || data['Customer Name'] || data['Customer'];
                return {
                    id,
                    'Contract No.': data.contract_no || id,
                    'Booking Number': data.booking_number,
                    'Customer': invygoCustomer || airportCustomer() || baseCustomer,
                    'Pick-up Branch': data.pickup_branch,
                    'Plate No.': data.plate_no,
                    'Model': data.model,
                    'Pick-up Date': data.pickup_date,
                    'Drop-off Date': data.dropoff_date,
                    'Phone Number': data.phone,
                    'Status': data.status,
                    'Invygo Plate': data.invygo_plate || '',
                    'Invygo Model': data.invygo_model || '',
                    'Close Date': data.dropoff_date || data.close_date || '',
                    'Car Model': data.model,
                    'Drop-Off Dte': data.dropoff_date,
                    _source: 'firestore'
                };
            };

            if (USE_CONTRACTS_V2) {
                // The store: the Business Bay / Invygo contracts whose number or booking number IS the term - else the
                // contracts of the Invygo booking id typed (Invygo's own id is Ejar's broker reference of the booking,
                // never Ejar's booking number - owner, 28 Sep 2026; src/services/invygoIds.js is the one path to it)
                let found = await v2ExactRecords(term, 'monthly');
                if (found.length === 0) {
                    let invygoContracts = [];
                    try {
                        invygoContracts = await contractsForInvygoId(term);
                    } catch (e) {
                        // the archive search below still runs: a failed Invygo lookup must not hide the E-Check history
                        logger.warn('[contracts] Invygo id lookup failed:', e?.message || e);
                    }
                    if (invygoContracts.length > 0) found = await v2RecordsByIds(invygoContracts, 'monthly');
                }
                found.forEach((v) => contracts.push(mapFirestoreDoc(v.contract_no, toLegacyContract(v))));
            } else {
            // 1. Fetch from Firestore (Active/Recent)
            // A. Direct Contract No Match (ID)
            const docRef = doc(contractsCollection, termUpper);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                contracts.push(mapFirestoreDoc(docSnap.id, docSnap.data()));
            }

            const processSnap = (snap) => {
                if (snap.empty) return;
                snap.forEach(d => {
                    if (contracts.some(c => c.id === d.id)) return;
                    contracts.push(mapFirestoreDoc(d.id, d.data()));
                });
            };

            // B. Booking Number Match
            const q = query(contractsCollection, where('booking_number', 'in', searchValues));
            processSnap(await getDocs(q));

            // C. Contract Number Match (Field Lookup)
            const qContract = query(contractsCollection, where('contract_no', '==', term));
            processSnap(await getDocs(qContract));

            // D. Invygo's own booking id typed (441738): the contracts of that booking. The Invygo sheet's upload
            //    used to write that id into contracts.booking_number (B above); with the sheet gone (owner,
            //    28 Sep 2026) Ejar's booking -> broker reference names them, as in the store branch above
            //    (src/services/invygoIds.js). A failed lookup must not hide the E-Check history below.
            if (contracts.length === 0) {
                try {
                    invygoNos = await contractsForInvygoId(term);
                } catch (e) {
                    logger.warn('[contracts] Invygo id lookup failed:', e?.message || e);
                }
                for (const no of invygoNos) {
                    const snap = await getDoc(doc(contractsCollection, no));
                    if (snap.exists() && !contracts.some(c => c.id === snap.id)) contracts.push(mapFirestoreDoc(snap.id, snap.data()));
                }
            }
            }

            // Fallback: If we found a contract in Firestore but didn't have Invygo customer yet,
            // use the booking_number from the Firestore record to try Invygo again (flags off: the old sheet).
            if (!USE_CONTRACTS_V2 && !invygoCustomer && contracts.length > 0) {
                const firestoreBooking = contracts.find(c => c._source === 'firestore' && c['Booking Number'])?.['Booking Number'];
                if (firestoreBooking && String(firestoreBooking) !== String(term)) {
                    try {
                        const invygoData = await invygoService.getInvygoDataByIdentifier(firestoreBooking);
                        if (invygoData && invygoData.customer_name) {
                            invygoCustomer = invygoData.customer_name;
                            contracts.forEach(c => {
                                if (!c['Customer'] || c['Customer'] === '') {
                                    c['Customer'] = invygoCustomer;
                                }
                            });
                        }
                    } catch (e) {
                    }
                }
            }

            // 2. Discover contract numbers through the E-Check archive
            //    Strategy A: By Booking Number
            //    Strategy B: By Contract Numbers found in Firestore
            //    Strategy C: Direct Term as Contract Number (Agreement # that's only in the archive)
            const archiveRecords = [];

            const byBooking = await archiveService.findByBookingNumber(term);
            if (byBooking) archiveRecords.push(...byBooking);

            if (contracts.length > 0 || invygoNos.length > 0) {
                // (an Invygo booking's contract with no Firestore record yet is still found through its E-Checks)
                const contractNos = [...new Set([...contracts.map(c => c['Contract No.']), ...invygoNos].filter(Boolean))];
                if (contractNos.length > 0) {
                    const byContract = await archiveService.findByContractNumbers(contractNos);
                    if (byContract) archiveRecords.push(...byContract);
                }
            }

            const byDirectContract = await archiveService.findByContractNumbers([term]);
            if (byDirectContract && byDirectContract.length > 0) {
                archiveRecords.push(...byDirectContract);
            }

            // Deduplicate Archive Records (by Contract No + Plate + Date + Check Type: a Create and a Close in the same
            // minute are two checks - CC 79361, 3 Oct 2026)
            const uniqueArchive = [];
            const seenArchive = new Set();
            archiveRecords.forEach(r => {
                const key = `${r['Contract No.'] || r['Contract No']}|${r['Plate Number'] || r['Plate No.']}|${r['Transaction Date']}|${r['Check Type'] || ''}`;
                if (!seenArchive.has(key)) {
                    seenArchive.add(key);
                    uniqueArchive.push(r);
                }
            });

            // --- SMART DISCOVERY (Customer Name Recovery via Plate) ---
            // (not with the store: every contract there carries its own customer)
            if (!USE_CONTRACTS_V2 && !invygoCustomer && !airportMatchData && uniqueArchive.length > 0) {
                const platesToSearch = [...new Set(uniqueArchive.map(r => r['Plate Number'] || r['Plate No.']).filter(Boolean))];
                if (platesToSearch.length > 0) {
                    try {
                        const qPlates = query(contractsCollection, where('plate_no', 'in', platesToSearch.slice(0, 30)));
                        const platesSnap = await getDocs(qPlates);
                        if (!platesSnap.empty) {
                            // Take metadata from the most recent match
                            const bestMatch = platesSnap.docs.map(d => d.data()).sort((a, b) => (b.pickup_date || '').localeCompare(a.pickup_date || ''))[0];

                            if (bestMatch && (bestMatch.customer || bestMatch.customer_name)) {
                                const discoveredName = bestMatch.customer || bestMatch.customer_name;
                                if (!invygoCustomer) invygoCustomer = discoveredName;

                                // Also try to re-enrich with Invygo if we found a booking ID
                                if (bestMatch.booking_number && String(bestMatch.booking_number) !== String(term)) {
                                    const invygoData = await invygoService.getInvygoDataByIdentifier(bestMatch.booking_number);
                                    if (invygoData && invygoData.customer_name) {
                                        invygoCustomer = invygoData.customer_name;
                                    }
                                }
                            }
                        }
                    } catch (e) {
                    }
                }
            }

            // 3. Contract numbers to load into the possession matcher
            const contractNumbersFound = new Set();
            contracts.forEach(c => { const no = upper(c['Contract No.']); if (no) contractNumbersFound.add(no); });
            uniqueArchive.forEach(r => { const no = upper(r['Contract No.'] || r['Contract No'] || r['Agreement']); if (no) contractNumbersFound.add(no); });
            airportMatches.forEach(a => { const no = upper(a['Contract No.'] || a.id); if (no) contractNumbersFound.add(no); });

            if (contractNumbersFound.size === 0) return [];

            const matcher = await buildMatcherForContracts([...contractNumbersFound]);

            // Metadata sources per contract number
            const firestoreByNo = new Map();
            contracts.forEach(c => { const no = upper(c['Contract No.']); if (no && !firestoreByNo.has(no)) firestoreByNo.set(no, c); });
            const airportByNo = new Map();
            airportMatches.forEach(a => { const no = upper(a['Contract No.'] || a.id); if (no && !airportByNo.has(no)) airportByNo.set(no, a); });
            const archiveByNo = new Map();
            uniqueArchive.forEach(r => {
                const no = upper(r['Contract No.'] || r['Contract No'] || r['Agreement']);
                if (!no) return;
                if (!archiveByNo.has(no)) archiveByNo.set(no, []);
                archiveByNo.get(no).push(r);
            });
            const firstVal = (rows, keys) => {
                for (const k of keys) {
                    const found = rows.find(r => r[k] !== undefined && r[k] !== null && r[k] !== '');
                    if (found) return found[k];
                }
                return '';
            };
            const rowPlate = (r) => normalizePlate(r['Plate Number'] || r['Plate No.'] || r['Plate No']);

            // Invygo name for a booking number the archive attached to a contract (cached per booking); the old
            // sheet is read with the flags off only - with the store the contract record carries its customer
            const invygoByBooking = new Map();
            const invygoNameFor = async (bookingNo) => {
                if (USE_CONTRACTS_V2) return '';
                const key = String(bookingNo);
                if (!invygoByBooking.has(key)) {
                    let name = '';
                    try {
                        const invygoData = await invygoService.getInvygoDataByIdentifier(bookingNo);
                        if (invygoData && invygoData.customer_name) name = invygoData.customer_name;
                    } catch (e) {
                    }
                    invygoByBooking.set(key, name);
                }
                return invygoByBooking.get(key);
            };

            // 4. One record per possession segment (car swaps included, repeated plates kept)
            const records = [];
            for (const no of contractNumbersFound) {
                const segments = matcher.segmentsOf(no);
                const info = matcher.contract(no);
                const fs = firestoreByNo.get(no);
                const airport = airportByNo.get(no);
                const rows = archiveByNo.get(no) || [];

                if (segments.length === 0) {
                    // Nothing known about possession: keep the raw Firestore record as before
                    if (fs) records.push(fs);
                    continue;
                }

                const bookingNumber = firstVal(rows, ['Booking Number', 'Booking No', 'Booking No.', 'Booking ID'])
                    || fs?.['Booking Number'] || airport?.['Booking Number'] || airport?.booking_number || term;

                // Customer: the contract record first, then the Invygo / airport fallbacks
                let customer = info?.customer || '';
                if (!customer && bookingNumber && String(bookingNumber) !== String(term)) customer = await invygoNameFor(bookingNumber);
                customer = customer || invygoCustomer || airportCustomer() || firstVal(rows, ['Customer Name', 'Customer']) || fs?.['Customer'] || '';

                const phone = firstVal(rows, ['Phone Number', 'Mobile Number', 'Mobile']) || fs?.['Phone Number'] || airport?.['Phone Number'] || '';
                const fsPlate = normalizePlate(fs?.['Plate No.']);
                const airportPlate = normalizePlate(airport?.['Plate No.']);

                segments.forEach((seg, i) => {
                    const readings = matcher.readings(seg);
                    const plateRows = rows.filter(r => rowPlate(r) === seg.plate);
                    const model = firstVal(plateRows, ['Model', 'Car Model'])
                        || (fs && fsPlate === seg.plate ? (fs['Model'] || fs['Car Model']) : '')
                        || (airport && airportPlate === seg.plate ? (airport['Model'] || airport['Car Model']) : '')
                        || '';
                    const pickup = formatDateTime(seg.start);
                    const dropoff = seg.open ? '' : formatDateTime(seg.end);
                    const segment = { contractNo: no, plate: seg.plate, plateRaw: seg.plateRaw, start: seg.start, end: seg.end, branch: seg.branch, open: seg.open };

                    records.push({
                        id: `${no}_${seg.plate}_${i}`,
                        'Contract No.': no,
                        'Booking Number': bookingNumber,
                        'Customer': customer,
                        'Pick-up Branch': seg.branch || info?.branch || fs?.['Pick-up Branch'] || '',
                        'Plate No.': seg.plateRaw || seg.plate,
                        'Model': model,
                        'Pick-up Date': pickup,
                        'Drop-off Date': dropoff,
                        'Phone Number': phone,
                        'Status': seg.open ? 'Open' : 'Closed',
                        'Odometer': readings.out?.odometer ?? readings.in?.odometer ?? '',
                        'Invygo Plate': fs?.['Invygo Plate'] || '',
                        'Invygo Model': fs?.['Invygo Model'] || '',
                        'Close Date': dropoff,
                        'Car Model': model,
                        'Drop-Off Dte': dropoff,
                        'E-Check Number Out': readings.out?.echeckNo || '',
                        'E-Check Number In': readings.in?.echeckNo || '',
                        _source: readings.out || readings.in ? 'archive' : 'firestore',
                        _segment: segment,
                        _readings: readings,
                    });
                });
            }

            // Sort by Date Ascending (Chronological Timeline: Oldest First)
            const startOf = (r) => (r._segment ? r._segment.start : parseDateTime(r['Pick-up Date']))?.getTime() || 0;
            records.sort((a, b) => startOf(a) - startOf(b) || String(a['Contract No.']).localeCompare(String(b['Contract No.'])));

            return records;
        } catch (error) {
            logger.error('Error fetching contracts history:', error);
            return [];
        }
    }
};

// --- MAINTENANCE SERVICE ---
export const maintenanceService = {
    // Helper to generate hash for maintenance records
    generateMaintenanceHash: (log) => maintenanceHash(log),

    addLog: async (log) => {
        try {
            // the store (USE_CONTRACTS_V2): POST /api/store/maintenance - the engine finds the same repair (plate |
            // damage | Date OUT) itself and updates it, else creates the log: {id, ...log, version, wasCreated | wasUpdated}
            if (USE_CONTRACTS_V2) {
                const saved = await storeAddLog(log);
                await jsonCacheService.clearMaintenanceCache();
                return { success: true, ...saved };
            }
            // Generate signature for smart sync
            const signature = maintenanceService.generateMaintenanceHash(log);

            // Check if record already exists
            const existingRecords = await maintenanceService.getLogsBySignature(signature);
            if (existingRecords && existingRecords.length > 0) {
                // Update existing record instead of creating new one
                const existingId = existingRecords[0].id;
                const updateData = {
                    ...log,
                    last_updated: new Date().toISOString()
                };
                const updateResult = await maintenanceService.updateLog(existingId, updateData);
                if (updateResult.success) {
                    // Clear cache to force refresh
                    await jsonCacheService.clearMaintenanceCache();
                    return { success: true, id: existingId, ...updateData, wasUpdated: true };
                } else {
                    return updateResult;
                }
            }

            // Create new record if not exists
            const docRef = doc(maintenanceCollection); // Auto-ID
            const newLog = {
                ...log,
                data_hash: signature, // Store hash for future comparisons
                created_at: new Date().toISOString(),
                last_updated: new Date().toISOString()
            };
            await setDoc(docRef, newLog);

            // Clear cache to force refresh
            await jsonCacheService.clearMaintenanceCache();

            return { success: true, id: docRef.id, ...newLog, wasCreated: true };
        } catch (error) {
            return { success: false, error: error.message };
        }
    },

    /**
     * Every maintenance log. The saved copy used to be trusted for 24 h, but the engine closes
     * logs by itself every hour straight in Firestore, so closed logs kept showing as open /
     * delayed for up to a day (audit FLT-07). Now, as for the fleet: the copy is used as it is
     * only while younger than MAINTENANCE_FRESH_MIN; older (up to 24 h since the last whole
     * read) only the logs changed since it was saved are read (`last_updated`, written by this
     * app and by the engine's auto-close) and merged in; otherwise the whole collection.
     */
    getAllLogs: async (forceRefresh = false) => {
        try {
            // the store (USE_CONTRACTS_V2): the saved copy + only the logs changed since its read (closed by the engine,
            // edited, deleted: GET /api/store/maintenance?since=); each log carries the `version` a save names
            if (USE_CONTRACTS_V2) return await storeLogs({ force: forceRefresh });
            // 1. Try JSON Cache first
            if (!forceRefresh) {
                const saved = await jsonCacheService.loadFromFile('maintenance.json');
                const rows = saved && Array.isArray(saved.data) ? saved.data : null;
                const ageMin = saved && saved.timestamp ? (Date.now() - new Date(saved.timestamp).getTime()) / 60000 : Infinity;
                if (rows && rows.length && ageMin >= 0 && ageMin < MAINTENANCE_FRESH_MIN) return rows;
                if (rows && rows.length && ageMin >= 0 && fullAgeMin(saved) < 24 * 60) {
                    const since = new Date(new Date(saved.timestamp).getTime() - 5 * 60000).toISOString();   // 5 min overlap
                    const changed = await getDocs(query(maintenanceCollection, where('last_updated', '>=', since)));
                    const byId = new Map(rows.map((r) => [String(r.id), r]));
                    changed.docs.forEach((d) => byId.set(d.id, { id: d.id, ...d.data() }));
                    const merged = [...byId.values()];
                    await jsonCacheService.saveMaintenance(merged);
                    logger.info(`[maintenance] ${changed.size} changed since ${since} merged into ${rows.length} saved logs`);
                    return merged;
                }
            }

            // 2. Fetch from DB
            const snapshot = await getDocs(maintenanceCollection);
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // 3. Save to JSON Cache (a whole read: deleted logs are gone from it)
            await jsonCacheService.saveMaintenance(data, { full: true });
            return data;
        } catch (error) {
            logger.error("Error fetching maintenance logs:", error);
            // Fallback to cache
            try {
                const cachedMaintenance = await jsonCacheService.loadMaintenance();
                if (cachedMaintenance) return cachedMaintenance;
            } catch (e) { }
            return [];
        }
    },

    // `version` (the store, USE_CONTRACTS_V2): the version of the log the page opened (its row); default the log's
    // version in the last read. The engine rebuilds data_hash itself. A log changed since is not written:
    // {success: false, code: 'record-changed', error} (the saved copy is dropped: the next read is fresh); a save
    // answers {success: true, version, log}.
    updateLog: async (id, data, { version } = {}) => {
        if (USE_CONTRACTS_V2) {
            try {
                const log = await storeSaveLog(id, data, version ?? data?.version);
                await jsonCacheService.clearMaintenanceCache();
                return { success: true, version: log?.version, log };
            } catch (error) {
                if (error?.code === 'record-changed') await jsonCacheService.clearMaintenanceCache();
                return { success: false, error: error.message, code: error?.code };
            }
        }
        try {
            const docRef = doc(db, 'maintenance_logs', id);
            // The signature is rebuilt from the log as it will be after this merge, never from
            // the partial update alone (a Date IN edit hashed to "||"); an update that does not
            // touch plate / damage / Date OUT leaves data_hash as it is (audit FLT-10).
            const saved = needsSavedLog(data) ? ((await getDoc(docRef)).data() || {}) : {};
            const dataHash = hashAfterUpdate(saved, data);
            const updateData = {
                ...data,
                ...(dataHash !== undefined ? { data_hash: dataHash } : {}),
                last_updated: new Date().toISOString()
            };
            await setDoc(docRef, updateData, { merge: true });

            // Clear cache to force refresh
            await jsonCacheService.clearMaintenanceCache();

            return { success: true };
        } catch (error) {
            return { success: false, error: error.message };
        }
    },

    getLogsBySignature: async (signature) => {
        try {
            // the store (USE_CONTRACTS_V2): addLog no longer needs this (the engine finds the same repair); the logs of
            // the saved copy carrying the signature
            if (USE_CONTRACTS_V2) return (await storeLogs()).filter((l) => l.data_hash === signature);
            // Query by data_hash field
            const q = query(maintenanceCollection, where('data_hash', '==', signature));
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            logger.error('Error querying maintenance logs by signature:', error);
            return [];
        }
    },

    bulkUpdateLogs: async (updates) => {
        try {
            if (!updates || updates.length === 0) return { success: true, count: 0 };

            // the store (USE_CONTRACTS_V2): one save per log, each on its version of the last read (a log changed
            // since is refused and the others still go through)
            if (USE_CONTRACTS_V2) {
                let count = 0;
                const refused = [];
                for (const item of updates) {
                    try {
                        await storeSaveLog(item.id, item.data, logVersionOf(item.id));
                        count += 1;
                    } catch (e) {
                        refused.push(`${item.id}: ${e.message}`);
                    }
                }
                await jsonCacheService.clearMaintenanceCache();
                return refused.length ? { success: false, count, error: new Error(refused.slice(0, 5).join('; ')) } : { success: true, count };
            }

            const CHUNK_SIZE = 450;
            let updatedCount = 0;

            for (let i = 0; i < updates.length; i += CHUNK_SIZE) {
                const batch = writeBatch(db);
                const chunk = updates.slice(i, i + CHUNK_SIZE);

                chunk.forEach(item => {
                    const docRef = doc(db, 'maintenance_logs', item.id);
                    batch.set(docRef, {
                        ...item.data,
                        last_updated: new Date().toISOString()
                    }, { merge: true });
                });

                await batch.commit();
                updatedCount += chunk.length;
            }
            return { success: true, count: updatedCount };
        } catch (error) {
            logger.error("Error bulk updating maintenance logs:", error);
            return { success: false, error };
        }
    },

    // `version` (the store, USE_CONTRACTS_V2): when given, a log changed since is not deleted
    deleteLog: async (id, { version } = {}) => {
        try {
            if (USE_CONTRACTS_V2) {
                await storeDeleteLog(id, version);   // already gone: nothing to do (as deleteDoc)
                await jsonCacheService.clearMaintenanceCache();
                return { success: true };
            }
            const docRef = doc(db, 'maintenance_logs', id);
            await deleteDoc(docRef);

            // Clear cache to force refresh
            await jsonCacheService.clearMaintenanceCache();

            return { success: true };
        } catch (error) {
            logger.error("Error deleting maintenance log:", error);
            return { success: false, error };
        }
    },

    // --- STATUS MAP PERSISTENCE (Single Doc for "One Read") ---
    saveStatusMap: async (mapData) => {
        // the store (USE_CONTRACTS_V2) keeps no status map: getStatusMap builds it from the fleet (nothing calls this)
        if (USE_CONTRACTS_V2) return { success: false, error: new Error('The status map is built from the fleet; it is not saved.') };
        try {
            // Check size before saving? Firestore limit 1MB.
            // mapData is { "PLATE": { status: "X", branch: "Y" } }
            // If huge, might need splitting or Storage. For now, assuming standard fleet size.
            const docRef = doc(db, 'settings', 'maintenance_status_map');
            await setDoc(docRef, {
                map: mapData,
                last_updated: new Date().toISOString()
            });
            return { success: true };
        } catch (error) {
            logger.error("Error saving status map:", error);
            return { success: false, error };
        }
    },

    /**
     * Plate -> { status, branch, km } of every car, built from the `fleet`
     * collection (the engine's hourly Ejar sync keeps status / branch /
     * current_km there). Keys: the plate as stored ("AA 12345") and its
     * normalized form ("aa12345"), the form the pages look up. The old
     * settings/maintenance_status_map doc is no longer read, so one source
     * feeds the fleet, maintenance and government pages alike.
     */
    getStatusMap: async () => {
        try {
            return statusMapOf(await fleetService.getAllCars());
        } catch (error) {
            logger.error("Error fetching status map:", error);
            return {};
        }
    }
};

// --- AIRPORT SERVICE (YELO CONTRACTS) ---
export const airportService = {
    getAllAirportContracts: async (forceRefresh = false, { since } = {}) => {
        // the store: the OPEN ones - with `since` ("yyyy-mm-dd") also the ones returned since that day
        if (USE_CONTRACTS_V2) {
            if (forceRefresh) forgetClosedReads();
            return since ? v2AirportRowsSince(since) : v2AirportRows();
        }
        try {
            // 1. The saved copy: as it is while fresh, else only the contracts changed since (the engine writes the
            // airport contracts from Ejar every hour with last_updated). It used to be kept as it was for 7 days,
            // so T1 / T2 figures could be a week old. forceRefresh skips the fresh window but still reads only
            // what changed - a Refresh used to read all ~49k airport contracts (CON-19).
            const saved = await jsonCacheService.loadFromFile('airport.json');
            const rows = saved && Array.isArray(saved.data) ? saved.data : null;
            const ageMin = saved && saved.timestamp ? (Date.now() - new Date(saved.timestamp).getTime()) / 60000 : Infinity;
            if (!forceRefresh && rows && rows.length && ageMin >= 0 && ageMin < AIRPORT_FRESH_MIN) return rows;
            if (rows && rows.length && ageMin >= 0 && fullAgeMin(saved) < 24 * 60) {
                const since = new Date(new Date(saved.timestamp).getTime() - 5 * 60000).toISOString();   // 5 min overlap
                const changed = await getDocs(query(airportContractsCollection, where('last_updated', '>=', since)));
                const byId = new Map(rows.map((r) => [r.id, r]));
                changed.docs.forEach((d) => byId.set(d.id, { id: d.id, ...d.data() }));
                const merged = [...byId.values()];
                await jsonCacheService.saveAirport(merged);
                logger.info(`[airport] ${changed.size} changed since ${since} merged into ${rows.length} saved rows`);
                return merged;
            }

            // 2. Load from DB
            const snapshot = await getDocs(airportContractsCollection);
            const data = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // 3. Save to JSON Cache
            await jsonCacheService.saveAirport(data, { full: true });   // a whole read

            return data;
        } catch (error) {
            logger.error('Error fetching airport contracts:', error);
            // Fallback to cache
            try {
                const cached = await jsonCacheService.loadAirport();
                if (cached) return cached;
            } catch (e) { }
            return [];
        }
    },

    getOpenAirportContracts: async () => {
        if (USE_CONTRACTS_V2) return v2AirportOpen();
        try {
            const q = query(airportContractsCollection, where('Status', '==', 'Open'));
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            // thrown, not []: the page shows the error instead of "No airport contracts" (CON-13)
            logger.error('Error fetching open airport contracts:', error);
            throw error;
        }
    },

    getClosedAirportContracts: async (limitCount = 500) => {
        if (USE_CONTRACTS_V2) return (await v2AirportClosedPage({ limitCount })).rows;
        try {
            // The MOST RECENTLY RETURNED contracts. `last_updated` is no guide: the engine's
            // nightly status backfill rewrites thousands of old docs, so ordering by it
            // showed 2025 contracts first. 'Drop-off Date' is a "dd/mm/yyyy HH:MM" string,
            // so it is read day by day (a prefix range per day, newest day first) until
            // `limitCount` closed contracts are in hand - about 500 reads, no index needed.
            const page = await airportService.getClosedAirportPage({ fromOffsetDays: 0, limitCount, maxSpanDays: 120 });
            if (page.rows.length > 0) return page.rows.slice(0, limitCount);
            // no day-stamped drop-offs found (unexpected format): most recently touched, minus open
            const isOpen = (d) => String(d.Status || d.status || '').toLowerCase() === 'open';
            const q = query(airportContractsCollection, orderBy('last_updated', 'desc'), limit(limitCount + 200));
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter((d) => !isOpen(d)).slice(0, limitCount);
        } catch (error) {
            logger.error('Error fetching closed airport contracts:', error);
            throw error;
        }
    },

    /**
     * One page of closed airport contracts, newest drop-off first: the days from `fromOffsetDays` back
     * (whole weeks) until `limitCount` are in hand or `maxSpanDays` were read. Returns
     * { rows, nextOffsetDays, done } - the next page starts where this one stopped, so "Load older"
     * reads ~limitCount docs per click. It replaces "Load All History", one unbounded
     * `Status != 'Open'` query over tens of thousands of contracts per click (CON-19).
     * Whole days only: a page may hold a few more than limitCount, never a gap.
     * The new store (v2AirportClosedPage) also returns `cursor`, the engine's own place after the last row: give it
     * back as `cursor` with the nextOffsetDays and the next page starts exactly there (a number alone starts from a
     * time, and cannot reach the contracts with no drop-off). This Firestore path has no cursor.
     */
    getClosedAirportPage: async ({ fromOffsetDays = 0, cursor, limitCount = 500, maxSpanDays = 366 } = {}) => {
        if (USE_CONTRACTS_V2) return v2AirportClosedPage({ fromOffsetDays, cursor, limitCount, maxSpanDays });
        const pad = (n) => String(n).padStart(2, '0');
        const dayKey = (d) => `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
        const isOpen = (d) => String(d.Status || d.status || '').toLowerCase() === 'open';
        const sortKey = (d) => {
            const m = /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?/.exec(String(d['Drop-off Date'] || d.dropoff_date || ''));
            return m ? `${m[3]}${m[2]}${m[1]}${m[4] || '00'}${m[5] || '00'}` : '';
        };
        const out = new Map();
        const today = new Date();
        let offset = fromOffsetDays;
        while (offset < fromOffsetDays + maxSpanDays && out.size < limitCount) {
            const weekStart = offset;
            const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(today); d.setDate(today.getDate() - weekStart - i); return dayKey(d); });
            const snaps = await Promise.all(days.map((day) => getDocs(query(
                airportContractsCollection,
                where('Drop-off Date', '>=', day),
                where('Drop-off Date', '<=', day + ''),
            ))));
            for (const snap of snaps) {
                for (const doc of snap.docs) {
                    const data = { id: doc.id, ...doc.data() };
                    if (!isOpen(data)) out.set(doc.id, data);
                }
            }
            offset += 7;
        }
        const rows = Array.from(out.values()).sort((a, b) => sortKey(b).localeCompare(sortKey(a)));
        // a whole span without one closed contract: nothing older is left to page through
        return { rows, nextOffsetDays: offset, done: rows.length === 0 };
    },

    // Airport contracts whose date field (e.g. 'Pick-up Date' or 'Drop-off Date')
    // falls on one day, via a string-prefix range on the stored "dd/mm/yyyy HH:MM".
    // Reads only that day's docs — cheap and exact, no full-archive load.
    getAirportByDay: async (field, dayDDMMYYYY) => {
        try {
            const q = query(
                airportContractsCollection,
                where(field, '>=', dayDDMMYYYY),
                where(field, '<', dayDDMMYYYY + ''),
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            // thrown, not []: the Daily Rental Report must not show "0 opened / 0 closed" for a failed read (CON-13)
            logger.error(`Error fetching airport by ${field}=${dayDDMMYYYY}:`, error);
            throw error;
        }
    },

    /**
     * The airport contracts of several days ("dd/mm/yyyy"): `[[picked up that day, drop-off that day], ...]` in the
     * days' order. Firestore: two reads a day (getAirportByDay); the store: one read for all of them. Throws on a
     * failed read (never "0 opened / 0 closed").
     */
    getAirportByDays: async (days) => {
        if (USE_CONTRACTS_V2) return v2AirportByDays(days);
        return Promise.all((days || []).map((dmy) => Promise.all([
            airportService.getAirportByDay('Pick-up Date', dmy),
            airportService.getAirportByDay('Drop-off Date', dmy),
        ])));
    },

    getReminderContracts: async () => {
        try {
            const q = query(airportContractsCollection, where('Status', 'in', ['Open', 'Delivered-Indebt']));
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            logger.error('Error fetching reminder contracts:', error);
            return [];
        }
    },

    /**
     * The newest airport contracts (by last_updated) - what the Matcher Master tab
     * shows. Replaces the full-collection read (~49k docs) that used to burn the
     * daily Firestore quota; older contracts are looked up on demand by number.
     * (Defined once: a second copy with a 2500 default used to be silently overridden by this one.)
     */
    getRecentAirportContracts: async (max = 500) => {
        try {
            const snap = await getDocs(query(airportContractsCollection, orderBy('last_updated', 'desc'), limit(max)));
            return snap.docs.map(d => ({ id: d.id, ...d.data() }));
        } catch (error) {
            logger.error('Error loading recent airport contracts:', error);
            return [];
        }
    },

    /** Airport contracts by contract number (doc id), open or closed; 30 per query, ids that are not airport are skipped. */
    getAirportContractsByIds: async (contractIds) => {
        if (USE_CONTRACTS_V2) return v2AirportByIds(contractIds);
        const ids = [...new Set((contractIds || []).map((c) => String(c || '').trim().toUpperCase()).filter(Boolean))];
        const out = [];
        for (let i = 0; i < ids.length; i += 30) {
            const snap = await getDocs(query(airportContractsCollection, where(documentId(), 'in', ids.slice(i, i + 30))));
            snap.forEach((d) => out.push({ id: d.id, ...d.data() }));
        }
        return out;
    },

    getAirportContractsByIdentifier: async (searchValue) => {
        try {
            // the store: the airport contracts whose number or booking number IS the text
            if (USE_CONTRACTS_V2) return (await v2ExactRecords(searchValue.toString().trim(), 'airport')).map(toLegacyAirport);
            const term = searchValue.toString().trim().toUpperCase();
            
            // 1. Direct ID Match
            const docRef = doc(airportContractsCollection, term);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) return [{ id: docSnap.id, ...docSnap.data() }];

            // 2. Booking Number Match
            const qBooking = query(airportContractsCollection, where('Booking Number', '==', searchValue));
            const snapBooking = await getDocs(qBooking);
            if (!snapBooking.empty) return snapBooking.docs.map(d => ({ id: d.id, ...d.data() }));

            // 3. Normalized Alternate Field checks
            // Different exports might use different casing
            const qAlt = query(airportContractsCollection, where('booking_number', '==', searchValue));
            const snapAlt = await getDocs(qAlt);
            return snapAlt.docs.map(d => ({ id: d.id, ...d.data() }));
            
        } catch (error) {
            logger.error('Error in getAirportContractsByIdentifier:', error);
            return [];
        }
    }
};

